/* eslint-disable max-len */
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { ActiveMediaFailedCode } from '@zoom/videosdk';

import { ZoomClient } from '@/index-types';

const {
  MicrophoneMuted,
  AudioStreamMuted,
  AudioPlaybackInterrupted,
  CameraPermissionReset,
  MicrophonePermissionReset,
  AudioConnectionFailed,
  AudioStreamEnded,
  AudioStreamFailed,
  VideoConnectionFailed,
  VideoStreamEnded,
  WebGlContextInvalid,
  WasmOutOfMemory,
  VideoStreamFailed,
  SharingStreamFailed,
} = ActiveMediaFailedCode;

const errorMessages = {
  [AudioConnectionFailed]: 'Please refresh the browser to reconnect.',
  [AudioStreamEnded]:
    'Refresh the browser; the audio stream was interrupted (possibly due to device issues or another app).',
  [AudioStreamFailed]: 'Close all browsers and rejoin the session.',
  [CameraPermissionReset]: 'Please grant the camera permission.',
  [MicrophonePermissionReset]: 'Grant the necessary microphone permissions.',
  [MicrophoneMuted]: 'Please click anywhere on the page to resume audio playback.',
  [AudioStreamMuted]:
    'The microphone was temporarily occupied and then released, typically due to scenarios like answering a phone call or another application using the microphone. Please click on the page so that the SDK can restore audio.',
  [AudioPlaybackInterrupted]:
    'The audio playback of other users in the session was interrupted by the system. Please click on the page to resume audio playback.',
  [VideoConnectionFailed]: 'Please refresh the browser to reconnect.',
  [VideoStreamEnded]: 'Please refresh the browser, the video stream was interrupted.',
  [VideoStreamFailed]: 'Please grant camera permission.',
  [WebGlContextInvalid]: '(WASM video only) Issue with the canvas required for video rendering.',
  [WasmOutOfMemory]: 'Please close all browsers and rejoin the session.',
  [SharingStreamFailed]: 'Please close all browsers and rejoin the session.',
};

function useActiveMediaFailed(
  zoomClient: ZoomClient,
  showError: (message: string, close?: () => void, closeText?: string) => void,
) {
  useEffect(() => {
    const handleMediaFailed = (
      payload: { code: number; message?: string } & Record<string, unknown>,
    ) => {
      const { code, message } = payload;
      Sentry.withScope(scope => {
        scope.setLevel('error');
        scope.setTag('zoom_event', 'active-media-failed');
        scope.setContext('zoomActiveMediaFailed', {
          code,
          message,
        });
        scope.setExtra('payload', payload);
        Sentry.captureMessage('Zoom active-media-failed');
      });

      const existCode = Object.keys(errorMessages).includes(code.toString());

      if (existCode) {
        showError(`${errorMessages[code]}. (${code})`);
      } else {
        showError(
          `There are some problems when starting media. Please refresh page and call again. (${code} - ${message})`,
          () => {
            window.location.reload();
          },
          'Refresh',
        );
      }

      Sentry.addBreadcrumb({
        category: 'zoom',
        level: 'info',
        message: 'active-media-failed',
        data: payload,
      });
    };
    zoomClient.on('active-media-failed', handleMediaFailed);
    return () => {
      zoomClient.off('active-media-failed', handleMediaFailed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomClient]);
}

export default useActiveMediaFailed;
